import { theme } from '@theme';
import { Dispatch, FC, SetStateAction, useEffect } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import ModalTitle from '../ModalTitle';

type ConfirmationModalProps = {
  isOpen: boolean;
  title: string;
  showCloseIcon?: boolean;
  cancelButtonText: string;
  submitButtonText: string;
  padding?: string;
  minHeight?: string;
  lineHeight?: string;
  width?: string;
  buttonWidth?: string;
  bodyComponent: JSX.Element;
  overlayClassName?: string;
  contentClassName?: string;
  handleCancelClick: Dispatch<SetStateAction<boolean>>;
  handleSubmitClick: Dispatch<SetStateAction<boolean>>;
  submitButtonBackgroundColor?: string;
  submitButtonTextColor?: string;
  submitButtonDisabled?: boolean;
  minWidth?: string;
  maxHeight?: string;
  isSubmitButtonVisible?: boolean;
  shouldCloseOnOverlayClick?: boolean;
};

const ConfirmationModal: FC<ConfirmationModalProps> = ({
  isSubmitButtonVisible = true,
  shouldCloseOnOverlayClick = false,
  ...props
}) => {
  const closeConfirmationModal = () => {
    props.handleCancelClick(false);
  };

  const onCancelButtonClick = () => {
    props.handleCancelClick(false);
  };

  const onSubmitButtonClick = () => {
    props.handleSubmitClick(false);
  };

  useEffect(() => {
    if (props.minHeight) {
      modalStyles.content.minHeight = props.minHeight;
    }
    if (props.minWidth) {
      modalStyles.content.minWidth = props.minWidth;
    }
    if (props.maxHeight) {
      modalStyles.content.maxHeight = props.maxHeight;
    }
  }, [props.minHeight, props.minWidth, props.maxHeight]);

  return (
    <Modal
      data-testid='confirmation-modal'
      isOpen={props.isOpen}
      onRequestClose={closeConfirmationModal}
      overlayClassName={props.overlayClassName ?? undefined}
      className={props.contentClassName ?? undefined}
      style={modalStyles}
      contentLabel='Confirmation  Modal'
      ariaHideApp={false}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
    >
      <ModalTitle
        data-testid='modal-title'
        bgColor={theme.palette.text.purple}
        title={props.title}
        size={theme.typography.h4}
        color={theme.palette.white}
        fontWeight={theme.typography.fontWeights.semibold}
        iconColor={theme.palette.white}
        height='56px'
        showCloseIcon={props?.showCloseIcon ? props.showCloseIcon : false}
        handleIconClick={closeConfirmationModal}
      />
      <ModalBody data-testid='modal-body' padding={props.padding} width={props.width}>
        <div className='message-body'>{props.bodyComponent}</div>
      </ModalBody>
      <ModalButtons>
        <ModalSecondaryButton
          buttonWidth={props.buttonWidth}
          data-testid='modal-discard-button'
          lineHeight={props.lineHeight}
          onClick={onCancelButtonClick}
          type='button'
        >
          {props.cancelButtonText}
        </ModalSecondaryButton>

        {isSubmitButtonVisible ? (
          <ModalSubmitButton
            buttonWidth={props.buttonWidth}
            data-testid='modal-submit-button'
            disabled={props.submitButtonDisabled}
            lineHeight={props.lineHeight}
            onClick={onSubmitButtonClick}
            submitButtonBackgroundColor={props.submitButtonBackgroundColor}
            submitButtonTextColor={props.submitButtonTextColor}
            type='button'
          >
            {props.submitButtonText}
          </ModalSubmitButton>
        ) : null}
      </ModalButtons>
    </Modal>
  );
};

export default ConfirmationModal;
type ModalBodyProps = {
  padding?: string;
  width?: string;
};

const ModalBody = styled.div<ModalBodyProps>`
  .message-body {
    padding: ${(props) => props.padding ?? '1.2rem 1.0rem'};
    width: ${(props) => props.width ?? 'auto'};
    display: table;
  }

  hr {
    margin-top: 0;
  }
`;

const ModalButtons = styled.div`
  gap: 0.8rem;
  padding: 1rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.palette.grayThree};
`;

type ModalButtonProps = {
  lineHeight?: string;
  buttonWidth?: string;
};
const ModalButton = styled.button<ModalButtonProps>`
  width: ${(props) => props.buttonWidth};
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 0.2rem;
  font-size: 0.88rem;
  line-height: ${(props) => props.lineHeight ?? '1.4rem'};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
`;

type ModalSubmitButtonProps = ModalButtonProps & {
  submitButtonBackgroundColor?: string;
  submitButtonTextColor?: string;
  submitButtonDisabled?: boolean;
};
const ModalSubmitButton = styled(ModalButton)<ModalSubmitButtonProps>`
  color: ${({ submitButtonTextColor, theme }) => submitButtonTextColor ?? theme.palette.text.white};
  background-color: ${({ submitButtonBackgroundColor, theme }) =>
    submitButtonBackgroundColor ?? theme.palette.text.purple};
  transition:
    background-color 200ms ease-in-out,
    color 200ms ease-in-out;
  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.palette.lightGrayTwo};
    background-color: ${({ theme }) => theme.palette.backgroundGray};
  }
`;

const ModalSecondaryButton = styled(ModalButton)`
  color: ${({ theme }) => theme.palette.wetAsphalt};
  background-color: ${({ theme }) => theme.palette.backgroundGray};
`;

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    minHeight: '247px',
    maxHeight: '640px',
    zIndex: 96,
    padding: '0',
    borderRadius: '5px',
    minWidth: 'auto',
  },
};
